"use client";

import React, { useEffect, useState, useMemo } from "react";
import { 
  Search, 
  Globe, 
  Clock, 
  AlertCircle, 
  Sparkles,
  Layers,
  CheckCircle2,
  Gift,
  Plane
} from "lucide-react";
import { fetchPublicShippingPolicies } from "@/api-service/shipping";
import { GroupedCountryShipping, ShippingTier } from "@/packages/types/shipping";
import { HEADER_V2_DESIGN_SYSTEM } from "@/components/header/v2/designTokens";
import { useCurrency } from "@/packages/context/CurrencyContext";
import { useCountry } from "@/packages/context/CountryContext";
import { useResolvedCountryName } from "@/packages/hooks/useResolvedCountryName";

interface DisplayTier extends ShippingTier {
  isSyntheticFree?: boolean;
}

export default function ShippingPolicyPage() {
  const currency = useCurrency();
  const { selectedCountryId, selectedCountryName } = useCountry();
  const resolvedCountryName = useResolvedCountryName();
  const [groupedCountries, setGroupedCountries] = useState<GroupedCountryShipping[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showAllCountries, setShowAllCountries] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    const loadShippingPolicies = async () => {
      try {
        setLoading(true);
        const res = await fetchPublicShippingPolicies();
        if (isMounted && res?.data?.groupedShipping) {
          setGroupedCountries(res.data.groupedShipping);
        } else if (isMounted && res?.data?.rules) {
          // Fallback client-side grouping
          const map = new Map<number, GroupedCountryShipping>();
          res.data.rules.forEach((r) => {
            const cId = r.countryId;
            const cName = r.country?.name || `Country #${cId}`;
            const cCode = r.country?.code || "GLOBAL";
            const curSymbol = r.currency?.symbol || "$";
            const curCode = r.currency?.code || "USD";

            if (!map.has(cId)) {
              map.set(cId, {
                countryId: cId,
                countryName: cName,
                countryCode: cCode,
                currencySymbol: curSymbol,
                currencyCode: curCode,
                minFee: r.shippingFee,
                maxFee: r.shippingFee,
                freeShippingThreshold: r.shippingFee === 0 ? r.minimumAmount : null,
                tiers: [],
              });
            }
            const item = map.get(cId)!;
            item.tiers.push({
              id: r.id,
              minimumAmount: r.minimumAmount,
              maximumAmount: r.maximumAmount,
              shippingFee: r.shippingFee,
            });
            if (r.shippingFee < item.minFee) item.minFee = r.shippingFee;
            if (r.shippingFee > item.maxFee) item.maxFee = r.shippingFee;
            if (r.shippingFee === 0 && (item.freeShippingThreshold === null || r.minimumAmount < item.freeShippingThreshold)) {
              item.freeShippingThreshold = r.minimumAmount;
            }
          });
          setGroupedCountries(Array.from(map.values()));
        }
      } catch (err) {
        console.error("Failed to load shipping policies", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadShippingPolicies();
    return () => {
      isMounted = false;
    };
  }, []);

  // Identify country/currency matching the current active user context
  const selectedMatch = useMemo(() => {
    if (!groupedCountries.length) return null;
    const currentCurrencyCode = (currency?.code || "USD").toUpperCase();
    const currentCountryName = (resolvedCountryName || selectedCountryName || "").toLowerCase().trim();

    if (selectedCountryId) {
      const matchById = groupedCountries.find(
        (c) => Number(c.countryId) === Number(selectedCountryId)
      );
      if (matchById) return matchById;
    }

    if (currentCountryName) {
      const matchByName = groupedCountries.find(
        (c) =>
          c.countryName.toLowerCase() === currentCountryName ||
          c.countryCode.toLowerCase() === currentCountryName
      );
      if (matchByName) return matchByName;
    }

    return (
      groupedCountries.find(
        (c) => c.currencyCode.toUpperCase() === currentCurrencyCode
      ) || null
    );
  }, [groupedCountries, currency?.code, resolvedCountryName, selectedCountryId, selectedCountryName]);

  // Filter grouped countries by search query or selected country toggle
  const filteredCountries = useMemo(() => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      return groupedCountries.filter(
        (c) =>
          c.countryName.toLowerCase().includes(q) ||
          c.countryCode.toLowerCase().includes(q) ||
          c.currencyCode.toLowerCase().includes(q)
      );
    }

    if (!showAllCountries && selectedMatch) {
      return [selectedMatch];
    }

    return groupedCountries;
  }, [groupedCountries, searchQuery, showAllCountries, selectedMatch]);

  return (
    <main className="min-h-screen bg-slate-50/50 pb-16 pt-6">
      {/* Header Banner */}
      <section className="relative overflow-hidden bg-[var(--brand-text-dark)] text-white py-12 md:py-16 px-4 mb-10 shadow-lg border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[var(--brand-accent)]/20 via-transparent to-transparent opacity-50 pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-amber-300 text-xs font-semibold uppercase tracking-wider mb-4">
            <Plane className="w-4 h-4" />
            <span>International Shipping Guide</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-white mb-4">
            International Shipping & Delivery Policies
          </h1>
          <p className="text-slate-300 text-sm md:text-base max-w-2xl mx-auto font-normal leading-relaxed">
            Country-grouped shipping rates, order spend tiers, and free shipping thresholds for international deliveries.
          </p>

          {/* Search Box */}
          <div className="mt-8 max-w-xl mx-auto relative">
            <div className="relative flex items-center">
              <Search className="absolute left-4 w-5 h-5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by country name (e.g., United States, India, UK)..."
                className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 text-sm font-medium shadow-xl focus:outline-none focus:ring-2 focus:ring-[var(--brand-accent)] border border-slate-200 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 text-xs font-semibold text-slate-400 hover:text-slate-600 uppercase"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className={`max-w-6xl mx-auto ${HEADER_V2_DESIGN_SYSTEM.spacing.containerPadding}`}>
        
        {/* Full-width International Order Dispatch Section */}
        <section className="mb-10">
          <div className="bg-white rounded-xl border border-slate-200/90 p-6 md:p-8 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-start gap-4 max-w-xl">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-[var(--brand-accent)] flex items-center justify-center shrink-0 shadow-2xs">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">
                  International Order Processing & Dispatch
                </h3>
                <p className="text-xs md:text-sm text-slate-600 leading-relaxed">
                  All international shipments are custom-packed and dispatched from our global warehouse within <strong>1–2 business days</strong> (excluding weekends and statutory holidays). Tracking details are sent automatically upon dispatch.
                </p>
              </div>
            </div>

            <div className="shrink-0 bg-slate-50 border border-slate-200/80 rounded-xl p-4 md:p-5 flex items-center gap-4 shadow-2xs">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 font-bold">
                <Plane className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                  Delivery Timeline
                </span>
                <span className="text-sm font-bold text-slate-900 block">
                  5 – 10 Business Days
                </span>
                <span className="text-[11px] text-emerald-700 font-semibold block mt-0.5">
                  International Tracked Express
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Country Grouped Shipping Rates Section */}
        <section className="mb-14">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                <Globe className="w-6 h-6 text-[var(--brand-accent)]" />
                <span>Shipping Rates by Country</span>
              </h2>
              <p className="text-xs md:text-sm text-slate-500 mt-1">
                All order spend tiers from database followed by Free Shipping for amounts above the highest tier.
              </p>
            </div>

            {/* Toggle Controls */}
            <div className="flex items-center gap-3">
              <div className="inline-flex p-1 bg-slate-200/70 rounded-xl border border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowAllCountries(false)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    !showAllCountries && !searchQuery.trim()
                      ? "bg-white text-[var(--brand-accent)] shadow-xs"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Selected Location ({currency?.code || "Active"})
                </button>
                <button
                  type="button"
                  onClick={() => setShowAllCountries(true)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    showAllCountries || searchQuery.trim()
                      ? "bg-white text-[var(--brand-accent)] shadow-xs"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  View All Countries ({groupedCountries.length})
                </button>
              </div>
            </div>
          </div>

          {/* Active Country Filter Info Bar */}
          {!showAllCountries && !searchQuery.trim() && selectedMatch && (
            <div className="mb-6 p-4 rounded-xl bg-amber-50/90 border border-amber-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-amber-950 shadow-2xs">
              <div className="flex items-center gap-2.5">
                <Sparkles className="w-4 h-4 text-[var(--brand-accent)] shrink-0" />
                <span>
                  Showing shipping rates for your active country/currency: <strong>{selectedMatch.countryName} ({selectedMatch.currencyCode})</strong>.
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowAllCountries(true)}
                className="text-xs font-bold text-[var(--brand-accent)] hover:underline shrink-0 text-left sm:text-right"
              >
                View All Destinations →
              </button>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-44 bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                  <div className="h-6 bg-slate-200 rounded w-1/3 mb-4" />
                  <div className="h-4 bg-slate-100 rounded w-full mb-2" />
                  <div className="h-4 bg-slate-100 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : filteredCountries.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-xs">
              <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-slate-800">No Shipping Rules Found</h3>
              <p className="text-xs text-slate-500 mt-1">
                {searchQuery
                  ? `No shipping rate found matching "${searchQuery}". Try searching for another country.`
                  : "Country-specific shipping rates will appear here once configured."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredCountries.map((country) => {
                const symbol = country.currencySymbol || "$";

                // 1. Sort DB tiers by minimumAmount ascending
                const sortedTiers: DisplayTier[] = [...country.tiers].sort((a, b) => a.minimumAmount - b.minimumAmount);

                // 2. Determine threshold after highest tier
                const lastDbTier = sortedTiers[sortedTiers.length - 1];
                const freeThreshold = lastDbTier.maximumAmount && lastDbTier.maximumAmount > 0 
                  ? lastDbTier.maximumAmount 
                  : lastDbTier.minimumAmount;

                // 3. Append final Free Shipping tier for amounts above the last fee threshold
                if (lastDbTier.shippingFee > 0) {
                  sortedTiers.push({
                    id: -999,
                    minimumAmount: freeThreshold,
                    maximumAmount: 0,
                    shippingFee: 0,
                    isSyntheticFree: true,
                  });
                }

                return (
                  <div
                    key={country.countryId}
                    className="group bg-white rounded-xl border border-slate-200/90 hover:border-[var(--brand-accent)] p-6 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between"
                  >
                    <div>
                      {/* Country Card Header */}
                      <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200/60 flex items-center justify-center font-bold text-sm text-[var(--brand-accent)] shadow-2xs">
                            {country.countryCode || "INTL"}
                          </div>
                          <div>
                            <h3 className="font-bold text-base text-slate-900 group-hover:text-[var(--brand-accent)] transition-colors">
                              {country.countryName}
                            </h3>
                            <span className="text-xs font-medium text-slate-400">
                              Currency: {country.currencyCode} ({country.currencySymbol})
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Free Shipping Available</span>
                        </div>
                      </div>

                      {/* Tier Breakdown Table */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-400 px-1 mb-1">
                          <span>Order Spend Segment</span>
                          <span>Shipping Rate</span>
                        </div>

                        {sortedTiers.map((tier, idx) => {
                          const isFree = tier.shippingFee === 0 || tier.isSyntheticFree;

                          let rangeLabel = "";
                          if (isFree) {
                            rangeLabel = `Above ${symbol}${tier.minimumAmount.toFixed(2)}`;
                          } else if (tier.maximumAmount && tier.maximumAmount > 0) {
                            rangeLabel = `${symbol}${tier.minimumAmount.toFixed(2)} – ${symbol}${tier.maximumAmount.toFixed(2)}`;
                          } else {
                            rangeLabel = `Above ${symbol}${tier.minimumAmount.toFixed(2)}`;
                          }

                          return (
                            <div
                              key={tier.id || idx}
                              className={`flex items-center justify-between p-3 rounded-lg border text-xs transition-all ${
                                isFree
                                  ? "bg-emerald-50/80 border-emerald-200 text-emerald-950 font-bold shadow-2xs"
                                  : "bg-slate-50/70 border-slate-200/60 text-slate-800 font-medium"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {isFree ? (
                                  <Gift className="w-4 h-4 text-emerald-600 shrink-0" />
                                ) : (
                                  <Layers className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                )}
                                <span>{rangeLabel}</span>
                              </div>

                              <span
                                className={`font-bold ${
                                  isFree
                                    ? "text-emerald-700 uppercase tracking-wide text-xs px-2.5 py-0.5 bg-emerald-100 rounded-md border border-emerald-300/60"
                                    : "text-slate-900"
                                }`}
                              >
                                {isFree ? "FREE SHIPPING" : `${symbol}${tier.shippingFee.toFixed(2)}`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                      <span className="flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-[var(--brand-accent)]" />
                        <span>{sortedTiers.length} Shipping Segments</span>
                      </span>
                      <span className="font-bold text-emerald-600">
                        Free Above {symbol}{freeThreshold.toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

